import {
  KeyValueTitle,
  CtrlNavigationEntry,
  CtrlNavigationContext,
  CtrlNavigationEntryRendererAuthConfig
} from '../../shared/_models/models';

export type CtrlNavigationContextConfig =
  KeyValueTitle<CtrlNavigationContext> & {
    navItems?: CtrlNavigationEntry[];
    homePageId?: string;
    allowEmpty?: boolean;
    authConfig?: CtrlNavigationEntryRendererAuthConfig;
    productLineLabel?: string;
  };

export const EXTERNAL_PUBLISHER_NAV_CONTEXT_LIST: CtrlNavigationContextConfig[] = [
  { key: 'ctrl', value: 'portal', title: 'SSP', homePageId: 'dashboard', productLineLabel: 'SUPPLY-SIDE PLATFORM' },
  { key: 'spearad', value: 'spearad', title: 'Ad Server', homePageId: 'dashboard', allowEmpty: true, authConfig: {
    userPermissions: {
      permissions: ['publisher.deal.show'],
      operand: 'AND',
    },
  } },
];

export const EXTERNAL_PARTNER_NAV_CONTEXT_LIST: CtrlNavigationContextConfig[] = [
  { key: 'partner', value: 'partner', title: 'Curated Marketplace', homePageId: 'exchange-deals', productLineLabel: 'CURATED MARKETPLACE' },
];

export const INTERNAL_NAV_CONTEXT_LIST: CtrlNavigationContextConfig[] = [
  { key: 'supply', value: 'supply', title: 'SSP', homePageId: 'dashboard' },
  { key: 'exchange', value: 'exchange', title: 'Exchange', homePageId: 'exchange-deals' },
];

export const collapsedContextSwitchNavConfig: CtrlNavigationEntry = {
  id: 'collapsed-context-switch',
  label: 'Product Line',
  path: null,
  iconLayers: [{ icon: 'tasks' }],
  children: [
    {
      id: 'ctrl',
      label: 'SSP',
      path: null,
      actionId: 'contextSwitch',
      actionPayload: { context: 'ctrl' },
      rendererFn: (_child, context) => context === 'ctrl' || context === 'spearad',
    },
    {
      id: 'spearad',
      label: 'Ad Server',
      path: null,
      actionId: 'contextSwitch',
      actionPayload: { context: 'spearad' },
      rendererFn: (_child, context) => context === 'ctrl' || context === 'spearad',
    },
    {
      id: 'supply',
      label: 'SSP',
      path: null,
      actionId: 'contextSwitch',
      actionPayload: { context: 'supply' },
      rendererFn: (_child, context) => context === 'supply' || context === 'exchange',
    },
    {
      id: 'exchange',
      label: 'Exchange',
      path: null,
      actionId: 'contextSwitch',
      actionPayload: { context: 'exchange' },
      rendererFn: (_child, context) => context === 'supply' || context === 'exchange',
    }
  ]
};


export const externalPublisherAccountNavConfig: CtrlNavigationEntry = {
  id: 'account',
  label: 'Account',
  path: null,
  rendererFn: () => false,
  children: [
    {
      id: 'account-general',
      label: 'Publisher',
      path: ['portal', 'account', 'general-information'],
      rendererFn: () => false,
    },
    {
      id: 'account-billing',
      label: 'Billing',
      path: ['portal', 'account', 'billing-information'],
      rendererFn: () => false,
    },
    {
      id: 'account-user',
      label: 'User Settings',
      path: ['portal', 'account', 'profile'],
      rendererFn: () => false,
    },
  ]
};


export const ctrlNavConfigs: CtrlNavigationEntry[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: ['portal'],
    lottieFile: 'nav_dashboard',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    path: null,
    lottieFile: 'nav_integrations',
    navigationPermissions: ['publisherIntegrationDetails'],
    children: [],
    rendererFn: (parent) => !!parent.children.length,
  },
  {
    id: 'deals',
    label: 'Deals',
    path: null,
    lottieFile: 'nav_deals',
    navigationPermissions: ['publisherDeal'],
    children: [
      {
        id: 'deals-packages',
        label: 'Inventory Packages',
        path: ['portal', 'inventory-packages'],
      },
      {
        id: 'deals-deals',
        label: 'Deals Management',
        path: ['portal', 'deals'],
      },
      {
        id: 'content-targeting',
        label: 'Content Targeting',
        path: ['portal', 'content-targeting'],
        featureToggleKey: 'KvpConfig'
      },
    ]
  },
  {
    id: 'reporting',
    label: 'Reports',
    path: null,
    lottieFile: 'nav_reports',
    rendererAuthConfig: {
      userPermissions: {
        operand: 'OR',
        permissions: [
          'publisher.reporting.default',
          'publisher.reporting.deals',
          'publisher.reporting.detailed',
          'publisher.reporting.demand',
        ],
      },
    },
    children: [
      {
        id: 'reporting-publisher',
        label: 'Publisher Summary',
        path: ['portal', 'reporting', 'publisher-summary'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['publisher.reporting.default'],
          },
        },
      },
      {
        id: 'reporting-deals',
        label: 'Deals Summary',
        path: ['portal', 'reporting', 'deals'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['publisher.reporting.deals'],
          },
        },
      },
      {
        id: 'reporting-detailed',
        label: 'Detailed Reporting',
        path: ['portal', 'reporting', 'detailed'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['publisher.reporting.detailed'],
          },
        },
      },
      {
        id: 'reporting-demand',
        label: 'Demand Reporting',
        path: ['portal', 'reporting', 'demand'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['publisher.reporting.demand'],
          },
        },
      },
    ]
  },
  externalPublisherAccountNavConfig
];


export const spearadNavConfigs: CtrlNavigationEntry[] = [
  externalPublisherAccountNavConfig
];


export const headerActionsConfig: CtrlNavigationEntry[] = [
  {
    id: 'account-action',
    label: 'Account',
    path: null,
    actionId: 'accountAction'
  },
  {
    id: 'logout-action',
    label: 'Logout',
    path: null,
    actionId: 'logoutAction'
  },
];


// export const updateAvailableConfig: CtrlNavigationEntry = {
//   id: 'update-available',
//   label: 'Update available',
//   tooltip: 'Please refresh',
//   path: null,
//   iconLayers: [
//     { icon: 'arrow-alt-circle-down' },
//     { icon: 'circle', transform: 'shrink-8 up-6 right-7', style: 'color: #12100B' },
//     { icon: 'circle', transform: 'shrink-10 up-6 right-7', style: 'color: #FF216B' },
//   ],
//   actionId: 'refreshAction',
//   class: 'font-icon-color-gold',
// };


export const globalSearchConfig: CtrlNavigationEntry = {
  id: 'global-search',
  label: 'Search',
  path: null,
  lottieFile: 'nav_search',
  actionId: 'toggleSearch',
  rendererAuthConfig: {
    excludedUserRoles: [
      'User Admin',
    ],
  },
};


export const loggingOutConfig: CtrlNavigationEntry = {
  id: 'logging-out',
  label: 'Logging out...',
  path: null,
  iconLayers: [{ icon: 'spinner', spin: true }],
  position: 'bottom',
  disabled: true,
};


export const iconDict: { [key: string]: string } = {
  home: 'nav_dashboard',
  dashboard: 'nav_dashboard',
  live_tv: 'nav_campaign_management',
  bubble_chart: 'nav_campaign_management',
  record_voice_over: 'nav_campaign_management',
  perm_media: 'nav_campaign_management',
  publishers: 'nav_campaign_management',
  attach_money: 'nav_monetization',
  view_module: 'nav_media',
  settings: 'nav_admin',
  cogs: 'nav_admin',
  theaters: 'nav_streaming_test',
  poll: 'nav_reports',
};


export const internalNavConfigs: CtrlNavigationEntry[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: ['supply'],
    lottieFile: 'nav_dashboard',
    rendererAuthConfig: {
      userPermissions: {
        operand: 'AND',
        permissions: ['supply.data.get']
      }
    }
  },
  {
    id: 'publishers',
    label: 'Publishers',
    path: null,
    rendererAuthConfig: {
      userPermissions: {
        operand: 'AND',
        permissions: ['supply.publisher.read'],
      },
    },
    lottieFile: 'nav_campaign_management',
    children: [
      {
        id: 'publishers-manage',
        label: 'Publisher List',
        path: ['supply', 'publishers'],
      },
      {
        id: 'publishers-packages',
        label: 'Inventory Packages',
        path: ['supply', 'inventory-packages'],
        rendererAuthConfig: {
          excludedUserRoles: [
            'finance'
          ],
        },
      },
      {
        id: 'publishers-deals',
        label: 'Deals',
        path: ['supply', 'deals'],
        rendererAuthConfig: {
          excludedUserRoles: [
            'finance'
          ],
        },
      },
      {
        id: 'content-targeting',
        label: 'Content Targeting',
        path: ['supply', 'content-targeting'],
        rendererAuthConfig: {
          excludedUserRoles: [
            'finance'
          ],
        }
      },
    ]
  },
  {
    id: 'reporting',
    label: 'Reports',
    path: null,
    rendererAuthConfig: {
      userPermissions: {
        operand: 'AND',
        permissions: ['supply.publisher.read'],
      },
    },
    lottieFile: 'nav_reports',
    children: [
      {
        id: 'reporting-supply-summary',
        label: 'Supply Summary',
        path: ['supply', 'reporting', 'supply-summary'],
      },
      {
        id: 'reporting-deals',
        label: 'Deals Summary',
        path: ['supply', 'reporting', 'deals'],
      },
      {
        id: 'reporting-detailed',
        label: 'Detailed Reporting',
        path: ['supply', 'reporting', 'detailed'],
      },
      {
        id: 'reporting-demand',
        label: 'Demand Reporting',
        path: ['supply', 'reporting', 'demand'],
      },
      {
        id: 'reporting-qps',
        label: 'QPS Reporting',
        path: ['supply', 'reporting', 'qps'],
      },
    ]
  },
  {
    id: 'approval-queue',
    label: 'Approval Queue',
    path: ['supply', 'approval-queue'],
    rendererAuthConfig: {
      userPermissions: {
        operand: 'AND',
        permissions: ['supply.approvalqueue.read'],
      },
    },
    iconLayers: [{ icon: 'tasks' }],
  },
  {
    id: 'action-log',
    label: 'Action Log',
    path: ['supply', 'action-log'],
    rendererAuthConfig: {
      excludedUserRoles: [
        'finance',
        'Exchange Business Team',
        'User Admin'
      ],
    },
    iconLayers: [{ icon: 'history' }],
  },
  {
    id: 'administration',
    label: 'Administration',
    path: null,
    lottieFile: 'nav_admin',
    children: [
      {
        id: 'roles',
        label: 'Roles',
        path: ['supply', 'admin', 'roles'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['system.roles.read'],
          },
        },
      },
      {
        id: 'users',
        label: 'Users',
        path: ['supply', 'admin', 'users'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['system.users.read'],
          },
        },
      },
      {
        id: 'traffic-sources',
        label: 'Traffic Sources',
        path: ['supply', 'traffic-sources'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['supply.trafficsource.read'],
          },
        },
      },
      // {
      //   id: 'ad-server-techs',
      //   label: 'Ad Server Techs',
      //   path: ['supply', 'ad-server-tech'],
      //   rendererAuthConfig: {
      //     userPermissions: {
      //       operand: 'AND',
      //       permissions: ['supply.adservertech.read'],
      //     },
      //   },
      // },
      {
        id: 'email-templates',
        label: 'Email Templates',
        path: ['supply', 'email-templates'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['system.email-templates.read'],
          },
          excludedUserRoles: [
            'finance'
          ],
        },
      },
      {
        id: 'terms-of-service',
        label: 'Legal',
        path: ['supply', 'terms-of-service'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['supply.termsofservice.read'],
          },
        },
      },
    ]
  },
  {
    id: 'account',
    label: 'Account',
    path: ['supply', 'account'],
    rendererFn: () => false,
  },
];


export const exchangeNavConfigs: CtrlNavigationEntry[] = [
  {
    id: 'exchange-deals-parent',
    label: 'Deals',
    path: null,
    lottieFile: 'nav_deals',
    children: [
      {
        id: 'exchange-deals',
        label: 'Deals List',
        path: ['exchange', 'exchange-deals'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.read'],
          },
        },
      },
      {
        id: 'exchange-forecasts',
        label: 'Forecasts',
        path: ['exchange', 'exchange-forecasts'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.read'],
          },
        },
      },
    ],
  },
  {
    id: 'exchange-reports',
    label: 'Reports',
    path: null,
    lottieFile: 'nav_reports',
    children: [
      {
        id: 'curated-marketplace-reporting',
        label: 'Deals Summary',
        path: ['exchange', 'reporting', 'curated-marketplace'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['exchange-deals-reporting.read'],
          },
        },
        featureToggleKey: 'CuratedMarketplaceReporting'
      },
      {
        id: 'exchange-adomain-reporting',
        label: 'Adomain Report',
        path: ['exchange', 'reporting', 'adomain'],
        rendererAuthConfig: { userPermissions: { permissions: ['exchange-deals-reporting.read'], operand: 'AND' } },
        featureToggleKey: 'AdomainReporting',
      },
    ]
  },
  {
    id: 'exchange-partnerships',
    label: 'Partnerships',
    path: null,
    iconLayers: [{ icon: 'handshake' }],
    children: [
      {
        id: 'exchange-pad',
        label: 'PAD',
        path: ['exchange', 'exchange-pads'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.read'],
          },
        },
      },
      {
        id: 'exchange-partners',
        label: 'CM Partners',
        path: ['exchange', 'exchange-partners'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.partner.read'],
          },
        },
      },
    ]
  },
  {
    id: 'exchange-lists',
    label: 'Lists',
    path: null,
    iconLayers: [{ icon: 'list-ul' }],
    children: [
      {
        id: 'exchange-universal-media-lists',
        label: 'Universal Media Lists',
        path: ['exchange', 'universal-media-lists'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['universal-media-list.read'],
          },
        },
        featureToggleKey: 'UniversalMediaLists'
      },
      {
        id: 'exchange-agencies',
        label: 'Agencies',
        path: ['exchange', 'exchange-agencies'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.agency.read'],
          },
        },
      },
      {
        id: 'exchange-brands',
        label: 'Brands',
        path: ['exchange', 'exchange-brands'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.brand.read'],
          },
        },
      },
      {
        id: 'exchange-personnel',
        label: 'Personnel',
        path: ['exchange', 'exchange-personnel'],
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['deal-marketplace.personnel.read'],
          },
        },
      }
    ]
  },
  {
    id: 'exchange-rtb-tools',
    label: 'RTB Tools',
    path: null,
    iconLayers: [{ icon: 'wrench' }],
    children: [
      {
        id: 'dsps',
        label: 'DSP',
        path: ['exchange', 'dsps'],
        lottieFile: 'nav_dsps',
        rendererAuthConfig: {
          userPermissions: {
            operand: 'AND',
            permissions: ['supply.dsps.read'],
          },
        }
      },
    ]
  },
  {
    id: 'account',
    label: 'Account',
    path: ['exchange', 'account'],
    rendererFn: () => false,
  },
];


export const partnerNavConfigs: CtrlNavigationEntry[] = [
  {
    id: 'exchange-deals',
    label: 'Deals',
    lottieFile: 'nav_deals',
    path: ['partner', 'curated-marketplace-deals'],
  },
  {
    id: 'exchange-forecasts',
    label: 'Forecasts',
    iconLayers: [{ icon: 'chart-line' }],
    path: ['partner', 'curated-marketplace-forecasts'],
    featureToggleKey: 'ExchangeForecasts'
  },
  {
    id: 'reporting-curated-marketplace',
    label: 'Reports',
    lottieFile: 'nav_reports',
    path: null,
    children: [
      {
        id: 'reporting-curated-marketplace',
        label: 'Deals Summary',
        path: ['partner', 'reporting', 'curated-marketplace'],
        featureToggleKey: 'CuratedMarketplaceReporting'
      },
      {
        id: 'reporting-adomain',
        label: 'Adomain Report',
        path: ['partner', 'reporting', 'adomain'],
        featureToggleKey: 'AdomainReporting',
      },
    ],
  },
  {
    id: 'universal-media-lists',
    label: 'Universal Media Lists',
    iconLayers: [{ icon: 'list-ul' }],
    path: ['partner', 'universal-media-lists'],
    featureToggleKey: 'UniversalMediaLists'
  },
  {
    id: 'account',
    label: 'Account',
    path: null,
    rendererFn: () => false,
    children: [
      {
        id: 'account-general',
        label: 'Partner',
        path: ['partner', 'account'],
        rendererFn: () => false,
      },
      {
        id: 'account-user',
        label: 'User Settings',
        path: ['partner', 'account', 'profile'],
        rendererFn: () => false,
      },
    ]
  },
];
